import { EventEnterIcon, EventLeaveIcon } from "@/components/icons";
import type { WorkflowNodeEvents } from "@/types/flow";
import { cn } from "@/utils/cn";
import type { ReactNode } from "react";

type EventKey = "enter" | "leave";

type NodeEventFrameProps = {
  events?: WorkflowNodeEvents;
  showTaskEvents?: boolean;
  selected?: boolean;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
};

const lifecycleStyles: Record<
  EventKey,
  {
    barClassName: string;
    iconClassName: string;
    countClassName: string;
    icon: ReactNode;
  }
> = {
  enter: {
    barClassName: "bg-green-light-6",
    iconClassName: "text-green",
    countClassName: "bg-[#22C55E]",
    icon: <EventEnterIcon />,
  },
  leave: {
    barClassName: "bg-[#FEF2F2]",
    iconClassName: "text-red",
    countClassName: "bg-[#F87171]",
    icon: <EventLeaveIcon />,
  },
};

const getEventCount = (events?: unknown): number => {
  if (!Array.isArray(events)) {
    return 0;
  }

  return events.filter(
    (event) =>
      typeof event === "string" ||
      (event &&
        typeof event === "object" &&
        "id" in event &&
        typeof event.id === "string"),
  ).length;
};

function EventCountPill({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: EventKey;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-sm font-medium leading-5 text-dark">{label}</span>
      <span
        className={cn(
          "flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium leading-none text-white",
          lifecycleStyles[tone].countClassName,
        )}
      >
        {count}
      </span>
    </div>
  );
}

function LifecycleBar({
  tone,
  label,
  events,
  showTaskEvents = false,
}: {
  tone: EventKey;
  label: string;
  events?: WorkflowNodeEvents;
  showTaskEvents?: boolean;
}) {
  const nodeCount = getEventCount(events?.node?.[tone]);
  const taskCount = showTaskEvents ? getEventCount(events?.task?.[tone]) : 0;
  const hasEvents = nodeCount > 0 || (showTaskEvents && taskCount > 0);

  if (!hasEvents) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex h-7 items-center gap-3 px-4 min-w-[280px]",
        lifecycleStyles[tone].barClassName,
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "flex h-4 w-4 items-center justify-center",
            lifecycleStyles[tone].iconClassName,
          )}
        >
          {lifecycleStyles[tone].icon}
        </div>
        <span className="text-sm font-medium leading-5 text-dark">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <EventCountPill label="Node" count={nodeCount} tone={tone} />
        {showTaskEvents && (
          <EventCountPill label="Task" count={taskCount} tone={tone} />
        )}
      </div>
    </div>
  );
}

export function NodeEventFrame({
  events,
  showTaskEvents = false,
  selected,
  className,
  bodyClassName,
  children,
}: NodeEventFrameProps) {
  return (
    <div className={cn("flex flex-col w-full", className)}>
      <LifecycleBar
        tone="enter"
        label="Enter"
        events={events}
        showTaskEvents={showTaskEvents}
      />
      <div className={cn("bg-white p-3 w-full", bodyClassName)}>{children}</div>
      <LifecycleBar
        tone="leave"
        label="Leave"
        events={events}
        showTaskEvents={showTaskEvents}
      />
    </div>
  );
}
